/**
 * ARGOV-EVENTS-CONNECT-01 Phase 2
 * ConferenceGuestJoin — Public guest join page (no authentication required).
 *
 * Flow:
 *   1. GET /api/public/connect/invitations/{token}  → meeting info
 *   2. Guest enters display name and clicks Join
 *   3. POST /api/public/connect/invitations/{token}/redeem → LK token
 *   4. Connects to LiveKit with can_publish=false (waiting room)
 *   5. WaitingScreen shown until host admits (permissions change)
 */
import "@livekit/components-styles";
import {
  LiveKitRoom,
  VideoConference,
  useLocalParticipant,
  RoomAudioRenderer,
} from "@livekit/components-react";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Video, Clock, AlertTriangle, RefreshCw, UserCheck, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

function isLiveKitAvailable(serverUrl, token) {
  if (!serverUrl || !token) return false;
  if (serverUrl.startsWith("wss://mock.") || token.startsWith("mock-")) return false;
  return true;
}

// ── WAITING SCREEN ─────────────────────────────────────────────────────────────
function GuestWaitingScreen({ displayName, meetingTitle }) {
  const { localParticipant } = useLocalParticipant();
  const isWaiting = !localParticipant?.permissions?.canPublish;

  if (!isWaiting) {
    // Admitted — show full conference
    return (
      <div className="flex-1 flex overflow-hidden" data-testid="guest-conference-active">
        <VideoConference />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center bg-slate-900 text-center p-8" data-testid="guest-waiting-screen">
      <div className="w-20 h-20 rounded-full bg-amber-600/20 border-2 border-amber-500/40 flex items-center justify-center mb-6 animate-pulse">
        <Clock className="w-8 h-8 text-amber-400" />
      </div>
      <h2 className="text-2xl font-semibold text-white mb-2">You're in the waiting room</h2>
      <p className="text-slate-400 max-w-sm">
        Hi <span className="font-medium text-white">{displayName}</span>, you're waiting to join{" "}
        <span className="font-medium text-slate-300">"{meetingTitle}"</span>.
        The host will admit you shortly.
      </p>
      <div className="flex items-center gap-2 mt-6 text-slate-500">
        <UserCheck className="w-4 h-4" />
        <span className="text-sm">This page updates automatically when you're admitted</span>
      </div>
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function ConferenceGuestJoin() {
  const { invToken } = useParams();

  const [phase,       setPhase]       = useState("loading");   // loading | info | joining | waiting | error | expired
  const [invInfo,     setInvInfo]     = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [nameError,   setNameError]   = useState("");
  const [conf,        setConf]        = useState(null);         // {server_url, token}
  const [errMsg,      setErrMsg]      = useState("");

  // Load invitation info
  useEffect(() => {
    if (!invToken) return setPhase("error");
    fetch(`${API}/api/public/connect/invitations/${invToken}`)
      .then(r => {
        if (!r.ok) return r.json().then(d => Promise.reject(d));
        return r.json();
      })
      .then(d => {
        setInvInfo(d);
        setDisplayName(d.invited_name || "");
        setPhase(d.status === "REDEEMED" ? "waiting_already" : "info");
      })
      .catch(err => {
        const msg = err?.detail?.message || err?.detail || "Invalid or expired invitation";
        setErrMsg(msg);
        setPhase("error");
      });
  }, [invToken]);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) { setNameError("Please enter your name"); return; }
    setNameError("");
    setPhase("joining");
    try {
      const r = await fetch(`${API}/api/public/connect/invitations/${invToken}/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_name: displayName.trim() }),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d?.detail?.message || d?.detail || "Could not join meeting");
      }
      const d = await r.json();
      setConf(d);
      setPhase("in_room");
    } catch (err) {
      setErrMsg(err.message || "Failed to join meeting");
      setPhase("error");
    }
  };

  // ── STATES ──────────────────────────────────────────────────────────────────

  if (phase === "loading") return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="guest-loading">
      <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
    </div>
  );

  if (phase === "error") return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-8" data-testid="guest-error">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-full bg-red-900/30 border border-red-700/40 flex items-center justify-center mx-auto mb-6">
          <AlertTriangle className="w-7 h-7 text-red-400" />
        </div>
        <h2 className="text-white text-xl font-semibold mb-3">Cannot Join Meeting</h2>
        <p className="text-slate-400 mb-4">{errMsg}</p>
        <p className="text-slate-600 text-sm">
          Contact the meeting organiser for a new invitation link.
        </p>
      </div>
    </div>
  );

  if (phase === "waiting_already") return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-8" data-testid="guest-already-used">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-full bg-amber-900/30 border border-amber-700/40 flex items-center justify-center mx-auto mb-6">
          <AlertTriangle className="w-7 h-7 text-amber-400" />
        </div>
        <h2 className="text-white text-xl font-semibold mb-3">Invitation Already Used</h2>
        <p className="text-slate-400">This invitation link has already been redeemed. Please ask the host for a new invitation if needed.</p>
      </div>
    </div>
  );

  if (phase === "in_room") {
    const isMock = !isLiveKitAvailable(conf?.server_url, conf?.token);
    return (
      <div className="h-screen flex flex-col bg-slate-950" data-testid="guest-conference-page">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Video className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">{conf?.meeting_title || "ARGOV Meeting"}</p>
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${isMock ? "bg-amber-400" : "bg-green-400"}`} />
                <span className="text-xs text-slate-400">{isMock ? "Mock mode" : "Live"}</span>
                <Badge className="text-xs bg-slate-700 text-slate-400 ml-1">Guest</Badge>
              </div>
            </div>
          </div>
        </div>

        {isMock ? (
          <div className="flex-1 flex items-center justify-center bg-slate-900" data-testid="guest-mock-notice">
            <div className="text-center max-w-sm p-8">
              <Video className="w-10 h-10 text-blue-400 mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">Mock Mode Active</h3>
              <p className="text-slate-400 text-sm">LiveKit credentials are not configured. Real media sessions require a LiveKit Cloud account.</p>
            </div>
          </div>
        ) : (
          <LiveKitRoom
            serverUrl={conf.server_url}
            token={conf.token}
            connect={true}
            audio={true}
            video={true}
            data-lk-theme="default"
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            <RoomAudioRenderer />
            <GuestWaitingScreen
              displayName={conf.display_name || displayName}
              meetingTitle={conf.meeting_title}
            />
          </LiveKitRoom>
        )}
      </div>
    );
  }

  // ── PRE-JOIN FORM ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="guest-prejoin-page">
      <div className="w-full max-w-md">
        {/* ARGOV Branding */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center mx-auto mb-4">
            <Video className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">ARGOV Connect</h1>
          <p className="text-slate-500 text-sm mt-1">Native Video Conference</p>
        </div>

        {/* Meeting Info Card */}
        {invInfo && (
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-5 mb-6">
            <h2 className="text-white font-semibold mb-1 truncate">{invInfo.meeting_title}</h2>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Calendar className="w-3.5 h-3.5" />
              <span>Hosted by {invInfo.host_name}</span>
            </div>
            {invInfo.scheduled_at && (
              <div className="flex items-center gap-2 text-slate-500 text-xs mt-1">
                <Clock className="w-3 h-3" />
                <span>{new Date(invInfo.scheduled_at).toLocaleString()}</span>
              </div>
            )}
            <div className="mt-3">
              <Badge className="text-xs bg-green-900/40 text-green-400 border border-green-800/40">
                {invInfo.meeting_status === "IN_PROGRESS" ? "Meeting is live" : "Scheduled"}
              </Badge>
            </div>
          </div>
        )}

        {/* Join Form */}
        <form onSubmit={handleJoin} className="bg-slate-900 border border-slate-700 rounded-xl p-6 space-y-5">
          <div>
            <h3 className="text-white font-semibold mb-1">Join as Guest</h3>
            <p className="text-slate-500 text-sm">Enter your name to join the waiting room</p>
          </div>

          <div>
            <Label className="text-sm text-slate-300">Your Name *</Label>
            <Input
              data-testid="guest-name-input"
              placeholder="e.g. Priya Sharma"
              value={displayName}
              onChange={e => { setDisplayName(e.target.value); setNameError(""); }}
              className="mt-1 bg-slate-800 border-slate-600 text-white placeholder-slate-500"
              autoFocus
            />
            {nameError && <p className="text-red-400 text-xs mt-1">{nameError}</p>}
          </div>

          <Button
            type="submit"
            data-testid="guest-join-btn"
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
            disabled={phase === "joining"}
          >
            {phase === "joining" ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Joining…</>
            ) : (
              <><Video className="w-4 h-4 mr-2" />Join Meeting</>
            )}
          </Button>

          <p className="text-xs text-slate-600 text-center">
            You'll be placed in a waiting room until the host admits you
          </p>
        </form>
      </div>
    </div>
  );
}
